import { makeAutoObservable, reaction, type IReactionDisposer, runInAction } from 'mobx'
import { type IInboxCombine } from 'entities/Inbox'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { MultiOrganizationsDropdownStore } from 'widgets/MultiOrganizationsDropdown'
import { type IMenuItemKeys, menuItemsV2 } from 'widgets/Sidebar/constants/menuItems'
import { getPathAIAutomation } from 'widgets/Menu/ui/MenuAIAutomation'
import { getPathOutbound } from 'widgets/Menu/ui/MenuOutbound'

export class SidebarStore {
  private _loading = true
  private _inboxesMap: Map<number, IInboxCombine> = new Map()
  private _disposeInboxes: IReactionDisposer | null = null
  private _multiOrganizationsDropdownStore: MultiOrganizationsDropdownStore

  constructor(private _entities: IEntitiesStore) {
    this._multiOrganizationsDropdownStore = new MultiOrganizationsDropdownStore(this._entities)

    makeAutoObservable(this)
  }

  get inboxesMap() {
    return this._inboxesMap
  }

  get multiOrganizationsDropdownStore() {
    return this._multiOrganizationsDropdownStore
  }

  get loading() {
    return this._loading
  }

  init = async () => {
    runInAction(() => {
      this._loading = true
    })

    await Promise.all([
      this._entities.featureFlagsStore.fetchFeatureFlags(),
      this._entities.subscriptionStore.initPromise,
      this._entities.organizationStore.initPromise,
    ])

    runInAction(() => {
      this._loading = false
    })
  }

  reset() {
    this._disposeInboxes?.()
    this._inboxesMap.clear()
  }

  initInboxes = () => {
    this._entities.inboxesStore.personalInboxes.forEach((item) => {
      this._inboxesMap.set(item.id, item)
    })
  }

  reInitInboxes = () => {
    this._entities.inboxesStore.fetchPersonalInboxes()
    this.initInboxes()
  }

  get unread() {
    return Array.from(this._inboxesMap.values())
      .map((item) => item.unread)
      .filter((item) => item)
      .reduce((state, value) => state + value, 0)
  }
  get unreadMessagesCount() {
    return Array.from(this._inboxesMap.values())
      .map((item) => item.unreadCount)
      .filter((item) => item)
      .reduce((state, value) => state + value, 0)
  }

  get menuItems() {
    if (this._entities.subscriptionStore.isAgencyWithoutSubscription) return []

    const canManageAnalytics = this._entities.userPermissionsStore.getItem('can_access_analytics')
    const pathAIAutomation = getPathAIAutomation(
      this._entities.usersStore.user_owner_id,
      this._entities
    )
    const pathOutbound = getPathOutbound(this._entities.usersStore.user_owner_id, this._entities)

    return menuItemsV2.filter((item) => {
      if (item.itemKey === 'analytics' && !canManageAnalytics) return false
      if (item.itemKey === 'AI_automation' && !pathAIAutomation) return false
      if (item.itemKey === 'outbound' && !pathOutbound) return false
      if (item.itemKey === 'discover' && this._entities.usersStore.user?.isViewOnlyRole)
        return false

      return true
    })
  }

  changeMenuItem = (key: IMenuItemKeys) => {
    if (key === 'discover') {
      this._entities.eventLogStore.logEvent(
        'Discover Section Used',
        {
          event_id: 'discover_section_used',
          userId: this._entities.usersStore.user?.id || 'anonymous_user',
          agencyId: this._entities.organizationStore.agency_id,
        },
        { groupId: this._entities.organizationStore.id }
      )
    }
  }

  handleReactionInboxes = () => {
    this._disposeInboxes?.()
    this._disposeInboxes = reaction(
      () => this._entities.inboxesStore.personalInboxes.length,
      () => {
        this.initInboxes()
      },
      {
        fireImmediately: true,
      }
    )
  }
}
